import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { card, tbl } from '../theme';

const STATUS_STYLE = {
  success:    { color: '#16A34A', bg: '#DCFCE7', icon: <CheckCircle  size={13} /> },
  failed:     { color: '#DC2626', bg: '#FEE2E2', icon: <XCircle      size={13} /> },
  processing: { color: '#1D4ED8', bg: '#EFF6FF', icon: <Clock        size={13} /> },
  pending:    { color: '#9CA3AF', bg: '#F3F4F6', icon: <Clock        size={13} /> },
  skipped:    { color: '#A16207', bg: '#FEF9C3', icon: <AlertTriangle size={13} /> },
};

export default function UploadPage() {
  const qc = useQueryClient();
  const [mode,      setMode]     = useState('cost-center'); // 'cost-center' | 'office-connect' | 'safety-kpi'
  const [file,      setFile]     = useState(null);
  const [ccId,      setCcId]     = useState('');
  const [periodId,  setPeriodId] = useState('');
  const [progress,  setProgress] = useState(0);
  const [ocResults, setOcResults] = useState(null);
  const [warnings,  setWarnings]  = useState([]);
  const inputRef = useRef(null);

  const { data: costCenters = [] } = useQuery({
    queryKey: ['cost-centers'],
    queryFn:  () => api.get('/cost-centers'),
  });

  const { data: periods = [] } = useQuery({
    queryKey: ['periods'],
    queryFn:  () => api.get('/periods'),
  });

  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['ingestion-log'],
    queryFn:  () => api.get('/upload/log'),
    refetchInterval: 10000, // Poll every 10s
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('cost_center_id', ccId);
      fd.append('period_id', periodId);
      return api.upload('/upload', fd, setProgress);
    },
    onSuccess: (data) => {
      toast.success(`Processed ${data.recordsProcessed} metrics successfully.`);
      setFile(null); setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
      qc.invalidateQueries(['ingestion-log']);
    },
    onError: (err) => {
      toast.error(err.message || 'Upload failed.');
      setProgress(0);
    },
  });

  // Covers every active yard from one file -- no cost_center_id to pick, see
  // routes/upload.js. Results come back per-yard rather than one count, since
  // one yard can fail (e.g. a label the file doesn't have) while the rest
  // succeed.
  const officeConnectMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('period_id', periodId);
      return api.upload('/upload/office-connect', fd, setProgress);
    },
    onSuccess: (data) => {
      setOcResults(data.results);
      const failed = data.results.filter((r) => !r.success).length;
      if (failed) {
        toast.error(`${data.results.length - failed} of ${data.results.length} yards processed; ${failed} failed.`);
      } else {
        toast.success(`All ${data.results.length} yards processed (${data.recordsProcessed} metrics).`);
      }
      setFile(null); setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
      qc.invalidateQueries(['ingestion-log']);
    },
    onError: (err) => {
      toast.error(err.message || 'Upload failed.');
      setProgress(0);
    },
  });

  // Also one file for every yard, so it shares office-connect's shape: period
  // only, per-yard results. Its warnings matter more than the other two's -- a
  // site missing from a month silently shortens that yard's year to date -- so
  // they are shown rather than left in the server log.
  const safetyKpiMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('period_id', periodId);
      return api.upload('/upload/safety-kpi', fd, setProgress);
    },
    onSuccess: (data) => {
      setOcResults(data.results.map((r) => ({ ...r, success: true, name: '' })));
      setWarnings(data.warnings || []);
      toast.success(`${data.recordsProcessed} values across ${data.results.length} yards.`);
      setFile(null); setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
      qc.invalidateQueries(['ingestion-log']);
    },
    onError: (err) => {
      toast.error(err.message || 'Upload failed.');
      setProgress(0);
    },
  });

  const allYards = mode === 'office-connect' || mode === 'safety-kpi';
  const activeMutation = mode === 'safety-kpi' ? safetyKpiMutation
    : mode === 'office-connect' ? officeConnectMutation : uploadMutation;
  const canUpload = allYards
    ? file && periodId && !activeMutation.isPending
    : file && ccId && periodId && !activeMutation.isPending;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Manual Excel Upload"
        subtitle="Use this when the FTP integration is unavailable. The file will be parsed and metrics loaded into the selected cost center's review."
      />

      <div style={s.card}>
        <div style={s.modeToggle}>
          <button
            style={{ ...s.modeBtn, ...(mode === 'cost-center' ? s.modeBtnActive : {}) }}
            onClick={() => { setMode('cost-center'); setOcResults(null); setWarnings([]); }}
          >
            Per Cost Center
          </button>
          <button
            style={{ ...s.modeBtn, ...(mode === 'office-connect' ? s.modeBtnActive : {}) }}
            onClick={() => { setMode('office-connect'); setOcResults(null); setWarnings([]); }}
          >
            Office Connect (All Yards)
          </button>
          <button
            style={{ ...s.modeBtn, ...(mode === 'safety-kpi' ? s.modeBtnActive : {}) }}
            onClick={() => { setMode('safety-kpi'); setOcResults(null); setWarnings([]); }}
          >
            Safety KPIs (All Yards)
          </button>
        </div>
        <div style={s.cardTitle}>
          {mode === 'safety-kpi' ? 'Upload Safety KPIs Workbook'
            : mode === 'office-connect' ? 'Upload Office Connect Export' : 'Upload File'}
        </div>
        {mode === 'office-connect' && (
          <div style={s.modeHint}>
            One file covers every yard — no Cost Center to pick. Fills whatever
            the Office Connect export supplies; use "Per Cost Center" for the
            gap-fill file afterward.
          </div>
        )}
        {mode === 'safety-kpi' && (
          <div style={s.modeHint}>
            The Safety VP's workbook — one file covers every yard, no Cost Center
            to pick. It is the only source for the three frequency rates, so
            without it they stay empty. Year to date is recomputed from the
            year's own totals, so every earlier month of the same year is read
            as well as the one selected.
          </div>
        )}

        {/* Drop zone */}
        <div
          style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : '#D1D5DB' }}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) setFile(f);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
          <Upload size={24} color={file ? '#16A34A' : '#9CA3AF'} />
          <div style={s.dropText}>
            {file ? (
              <><strong style={{ color: '#16A34A' }}>{file.name}</strong><br /><span style={{ color: '#9CA3AF' }}>{(file.size / 1024).toFixed(0)} KB</span></>
            ) : (
              <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: '#9CA3AF' }}>.xlsx or .xls files only</span></>
            )}
          </div>
        </div>

        {/* Selectors */}
        <div style={s.selectors}>
          {!allYards && (
            <div style={s.field}>
              <label style={s.label}>Cost Center</label>
              <select style={s.select} value={ccId} onChange={(e) => setCcId(e.target.value)}>
                <option value="">Select cost center…</option>
                {costCenters.map((cc) => (
                  <option key={cc.id} value={cc.id}>{cc.name}</option>
                ))}
              </select>
            </div>
          )}
          <div style={s.field}>
            <label style={s.label}>Review Period</label>
            <select style={s.select} value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
              <option value="">Select period…</option>
              {periods.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Progress */}
        {activeMutation.isPending && (
          <div style={s.progressWrap}>
            <div style={s.progressTrack}>
              <div style={{ ...s.progressFill, width: `${progress}%` }} />
            </div>
            <span style={s.progressLabel}>{progress}%</span>
          </div>
        )}

        <button
          style={{ ...s.uploadBtn, opacity: canUpload ? 1 : 0.5 }}
          disabled={!canUpload}
          onClick={() => activeMutation.mutate()}
        >
          {activeMutation.isPending ? 'Processing…' : '↑ Upload and Process'}
        </button>

        {/* Per-yard results, Office Connect mode only */}
        {allYards && warnings.length > 0 && (
          <div style={s.warnBox}>
            <div style={s.warnTitle}>
              <AlertTriangle size={13} /> {warnings.length} thing{warnings.length === 1 ? '' : 's'} worth reading
            </div>
            {warnings.map((w, i) => <div key={i} style={s.warnItem}>{w}</div>)}
          </div>
        )}

        {allYards && ocResults && (
          <div style={s.ocResults}>
            {ocResults.map((r) => (
              <div key={r.costCenterId} style={s.ocRow}>
                <span style={{ color: r.success ? '#16A34A' : '#DC2626' }}>
                  {r.success ? <CheckCircle size={13} /> : <XCircle size={13} />}
                </span>
                <span style={s.ocYard}>{r.code}{r.name ? ` ${r.name}` : ''}</span>
                <span style={s.ocDetail}>
                  {r.success ? `${r.recordsProcessed} metrics` : r.error}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Ingestion log ────────────────────────────────────── */}
      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Ingestion History</div>
        {logsLoading ? <LoadingSpinner /> : (
          <table style={s.logTable}>
            <thead>
              <tr>
                {['File', 'Cost Center', 'Period', 'Source', 'Status', 'Records', 'Processed'].map((h) => (
                  <th key={h} style={s.logTh}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const st = STATUS_STYLE[log.status] || STATUS_STYLE.pending;
                return (
                  <tr key={log.id} style={s.logTr}>
                    <td style={s.logTd} title={log.file_name}>{log.file_name?.slice(-40) || '—'}</td>
                    <td style={s.logTd}>{log.cost_center_name || '—'}</td>
                    <td style={s.logTd}>{log.period_label || '—'}</td>
                    <td style={s.logTd}>{log.source === 'ftp' ? '⟳ FTP' : '↑ Manual'}</td>
                    <td style={s.logTd}>
                      <span style={{ ...s.statusPill, color: st.color, background: st.bg }}>
                        {st.icon} {log.status}
                      </span>
                    </td>
                    <td style={{ ...s.logTd, textAlign: 'right' }}>{log.records_processed ?? '—'}</td>
                    <td style={s.logTd}>{log.processed_at ? new Date(log.processed_at).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr><td colSpan={7} style={s.emptyLog}>No ingestion history yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const s = {
  page:         { maxWidth: '900px', margin: '0 auto' },
  card:         { ...card.base, padding: '24px', marginBottom: '28px' },
  cardTitle:    { fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '16px' },
  modeToggle:   { display: 'flex', gap: '8px', marginBottom: '16px' },
  modeBtn: {
    padding: '6px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '999px',
    border: '1px solid #D1D5DB', background: '#fff', color: '#6B7280',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  modeBtnActive:{ background: '#1B2A4A', borderColor: '#1B2A4A', color: '#fff' },
  modeHint:     { fontSize: '12px', color: '#6B7280', marginBottom: '14px', lineHeight: 1.5 },
  ocResults:    { marginTop: '16px', borderTop: '1px solid #E5E7EB', paddingTop: '14px' },
  warnBox:      { marginTop: '16px', background: '#FEF9C3', border: '1px solid #FCD34D', borderRadius: '8px', padding: '10px 12px' },
  warnTitle:    { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#A16207', marginBottom: '6px' },
  warnItem:     { fontSize: '12px', color: '#854D0E', lineHeight: 1.5, marginTop: '3px' },
  ocRow:        { display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', fontSize: '12px' },
  ocYard:       { fontWeight: 600, color: '#374151', minWidth: '180px' },
  ocDetail:     { color: '#6B7280' },
  dropZone: {
    border: '2px dashed', borderRadius: '10px', padding: '32px',
    textAlign: 'center', cursor: 'pointer', marginBottom: '16px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
    transition: 'border-color 0.2s',
  },
  dropText:     { fontSize: '13px', color: '#374151', lineHeight: 1.6 },
  selectors:    { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' },
  field:        { flex: 1, minWidth: '200px' },
  label:        { display: 'block', fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  select:       { width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', background: '#fff' },
  progressWrap: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' },
  progressFill: { height: '100%', background: '#1B2A4A', borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '11px', fontWeight: 600, color: '#374151', minWidth: '30px' },
  uploadBtn: {
    padding: '10px 20px', fontSize: '13px', fontWeight: 600, border: 'none', borderRadius: '8px',
    background: '#1B2A4A', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', transition: 'opacity 0.15s',
  },
  logSection:   { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' },
  logTitle:     { fontSize: '13px', fontWeight: 600, color: '#374151', padding: '14px 18px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' },
  logTable:     { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  logTh:        { ...tbl.th, ...tbl.th },
  logTr:        { borderBottom: '1px solid #F3F4F6' },
  logTd:        { ...tbl.td, ...tbl.td },
  statusPill:   { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500 },
  emptyLog:     { textAlign: 'center', padding: '24px', color: '#9CA3AF' },
};
